import { useQueryClient } from "@tanstack/react-query";
import { createContext, use, useEffect, useEffectEvent, useState, useSyncExternalStore, type ReactNode } from "react";
import type { AppWsEvent, AppWsEventName } from "../api/appEvents.ts";
import { ApiError } from "../api/errors.ts";
import { useAppEnvironment } from "../environment.tsx";
import { useApiClient } from "../session/SessionProvider.tsx";
import { applyAppEvent, resyncAll } from "./applyAppEvent.ts";
import { startRealtimeConnection, type ConnectionState } from "./connection.ts";

export type { ConnectionState } from "./connection.ts";

/** Состояние связи и шина событий App WS для компонентов; соединение пишет в них из эффекта. */
type RealtimeHub = ReturnType<typeof createRealtimeHub>;

const RealtimeContext = createContext<RealtimeHub | null>(null);

/**
 * App WS залогиненного пользователя: события обновляют кэш серверных данных, `sync_required`
 * и каждое переподключение перезагружают его целиком. Монтируется только с активной сессией.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { apiBaseUrl, WebSocket, timers } = useAppEnvironment();
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [hub] = useState(createRealtimeHub);

  useEffect(
    () =>
      startRealtimeConnection({
        fetchToken: async () => {
          const { data, error, response } = await client.GET("/v1/auth/ws-token");
          if (!data) throw ApiError.from(response, error);
          return data.token;
        },
        openSocket: (token) => new WebSocket(appSocketUrl(apiBaseUrl, token)),
        onEvent: (event) => {
          applyAppEvent(queryClient, event);
          hub.emit(event);
        },
        onResync: () => resyncAll(queryClient),
        onStateChange: hub.setState,
        timers,
      }),
    [apiBaseUrl, WebSocket, timers, client, queryClient, hub],
  );

  return <RealtimeContext value={hub}>{children}</RealtimeContext>;
}

function useRealtime(): RealtimeHub {
  const realtime = use(RealtimeContext);
  if (!realtime) throw new Error("RealtimeProvider is missing");
  return realtime;
}

/** «Нестабильное соединение»: App WS не подключён дольше 5 секунд или браузер офлайн. Отправку это не блокирует. */
export function useConnectionState(): ConnectionState {
  const realtime = useRealtime();
  return useSyncExternalStore(realtime.subscribeState, realtime.getState);
}

/** Подписка на событие App WS; кэш к этому моменту уже обновлён. */
export function useAppEvent<Name extends AppWsEventName>(
  name: Name,
  handler: (event: Extract<AppWsEvent, { event: Name }>) => void,
): void {
  const realtime = useRealtime();
  const onEvent = useEffectEvent(handler);
  useEffect(
    () =>
      realtime.subscribeEvents((event) => {
        if (event.event === name) onEvent(event as Extract<AppWsEvent, { event: Name }>);
      }),
    [realtime, name],
  );
}

function createRealtimeHub() {
  let state: ConnectionState = "connected";
  const stateListeners = new Set<() => void>();
  const eventListeners = new Set<(event: AppWsEvent) => void>();
  return {
    getState: () => state,
    setState(next: ConnectionState) {
      state = next;
      for (const listener of [...stateListeners]) listener();
    },
    subscribeState(listener: () => void) {
      stateListeners.add(listener);
      return () => {
        stateListeners.delete(listener);
      };
    },
    emit(event: AppWsEvent) {
      for (const listener of [...eventListeners]) listener(event);
    },
    subscribeEvents(listener: (event: AppWsEvent) => void) {
      eventListeners.add(listener);
      return () => {
        eventListeners.delete(listener);
      };
    },
  };
}

/** `http(s)://api` → `ws(s)://api/v1/ws/app?token=…`; в dev и prod — тот же origin, что у приложения. */
function appSocketUrl(apiBaseUrl: string, token: string): string {
  const url = new URL("/v1/ws/app", apiBaseUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("token", token);
  return url.toString();
}
